/**
 * SADA Careers · Job Postings API (public + admin CRUD).
 */
import axios from 'axios';

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/careers`;

const authHeader = () => {
  const token = localStorage.getItem('dermascan_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const jobsApi = {
  async listPublic() {
    const { data } = await axios.get(`${BASE}/jobs`);
    return data;
  },
  async getBySlug(slug) {
    const { data } = await axios.get(`${BASE}/jobs/${slug}`);
    return data;
  },
  async listAdmin() {
    const { data } = await axios.get(`${BASE}/admin/jobs`, { headers: authHeader() });
    return data;
  },
  async create(payload) {
    const { data } = await axios.post(`${BASE}/admin/jobs`, payload, { headers: authHeader() });
    return data;
  },
  async update(id, payload) {
    const { data } = await axios.patch(`${BASE}/admin/jobs/${id}`, payload, {
      headers: authHeader(),
    });
    return data;
  },
  async remove(id) {
    const { data } = await axios.delete(`${BASE}/admin/jobs/${id}`, { headers: authHeader() });
    return data;
  },
  async seedDefaults() {
    const { data } = await axios.post(`${BASE}/admin/jobs/seed`, {}, { headers: authHeader() });
    return data;
  },
};

export const POST_TYPE_LABELS = {
  auxiliaire: 'Auxiliaire en pharmacie',
  pharmacien: 'Pharmacien(ne)',
  stagiaire: 'Stagiaire',
  administratif: 'Administratif / Gestion',
  livreur: 'Livreur / Coursier',
  autre: 'Autre',
};

export const EMPLOYMENT_LABELS = {
  CDI: 'CDI',
  CDD: 'CDD',
  STAGE: 'Stage',
  FREELANCE: 'Freelance',
  TEMPS_PARTIEL: 'Temps partiel',
};

// Convertit un job posting en JSON-LD Schema.org JobPosting (Google Jobs)
function escapeHtml(str) {
  return String(str || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export function buildJobPostingJsonLd(job, canonicalUrl) {
  const org = {
    '@type': 'Pharmacy',
    name: 'Pharmacie Saint Ange Des Amis (SADA)',
    sameAs: 'https://phciesada.org',
    logo: 'https://phciesada.org/images/sada-avatar.png',
  };
  const map = {
    CDI: 'FULL_TIME',
    CDD: 'CONTRACTOR',
    STAGE: 'INTERN',
    FREELANCE: 'CONTRACTOR',
    TEMPS_PARTIEL: 'PART_TIME',
  };
  const bullets = (items) => (items || []).map((m) => `<li>${escapeHtml(m)}</li>`).join('');
  const description = [
    escapeHtml(job.description_short),
    ...(job.missions?.length ? [`<h4>Missions</h4><ul>${bullets(job.missions)}</ul>`] : []),
    ...(job.profile?.length ? [`<h4>Profil recherché</h4><ul>${bullets(job.profile)}</ul>`] : []),
    ...(job.offer?.length ? [`<h4>Ce que SADA offre</h4><ul>${bullets(job.offer)}</ul>`] : []),
  ].join('\n');

  const payload = {
    '@context': 'https://schema.org',
    '@type': 'JobPosting',
    title: escapeHtml(job.title),
    description,
    identifier: {
      '@type': 'PropertyValue',
      name: 'Pharmacie SADA',
      value: job.slug,
    },
    datePosted: (job.published_at || job.created_at || '').slice(0, 10),
    employmentType: map[job.employment_type] || 'FULL_TIME',
    hiringOrganization: org,
    jobLocation: {
      '@type': 'Place',
      address: {
        '@type': 'PostalAddress',
        streetAddress: job.location || 'Cocody Angré 7e Tranche',
        addressLocality: job.city || 'Abidjan',
        addressCountry: job.country || 'CI',
      },
    },
    directApply: true,
    url: canonicalUrl,
  };
  if (job.deadline_iso) payload.validThrough = job.deadline_iso;
  if (job.salary_min || job.salary_max) {
    payload.baseSalary = {
      '@type': 'MonetaryAmount',
      currency: job.salary_currency || 'XOF',
      value: {
        '@type': 'QuantitativeValue',
        minValue: job.salary_min || undefined,
        maxValue: job.salary_max || undefined,
        unitText: job.salary_period || 'MONTH',
      },
    };
  }
  if (job.experience_years) {
    payload.experienceRequirements = {
      '@type': 'OccupationalExperienceRequirements',
      monthsOfExperience: job.experience_years * 12,
    };
  }
  if (job.education_level) {
    payload.educationRequirements = {
      '@type': 'EducationalOccupationalCredential',
      credentialCategory: job.education_level,
    };
  }
  return payload;
}
